"use client";

import { useCallback, useEffect, useRef } from "react";
import { METRICS_EVENTS } from "@cede/types";
import {
  CONSECUTIVE_SUCCESS_THRESHOLD,
  INITIAL_CHECK_TIMEOUT_MS,
  MAX_SILENT_RECONNECTS,
  SSE_HEARTBEAT_TIMEOUT_MS,
} from "./constants";
import { subscribeToHealthConnectionState, subscribeToHealthFrames } from "./healthSseBridge";
import { HEALTH_STATUS, type HealthPayload } from "./types";
import { useApiHealthStore, API_HEALTH_STATUS, type ApiHealthStatus, type ApiHealthSource } from "./useApiHealthStore";
import { useMetricsEvents } from "@/hooks/data/useMetricsEvents";
import { useQueryClient } from "@tanstack/react-query";

const isValidHealthStatus = (value: string): value is HealthPayload["status"] => {
  return value === HEALTH_STATUS.OK;
};

/**
 * Drives the API-down banner from the health heartbeat that rides the
 * notifications SSE (merged there so the app opens one socket, not two). This
 * hook is a PURE OBSERVER: `useNotifications` owns the socket and reconnection,
 * forwarding each `health` frame + connection open/close through the health
 * bridge. Here we keep the heartbeat-timeout detection, recovery hysteresis, and
 * offline/online handling, but no longer own an EventSource or reconnect it.
 */
export const useApiHealthStatus = () => {
  const queryClient = useQueryClient();
  const { trackEvent } = useMetricsEvents();
  const { status, source, setStatus, setChecking } = useApiHealthStore();

  const decisionRef = useRef<"checking" | "decided">("checking");
  const initialTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastHeartbeatRef = useRef<number>(Date.now());
  const consecutiveSuccessRef = useRef(0);
  const lastNonOfflineStatusRef = useRef<ApiHealthStatus>(API_HEALTH_STATUS.UNKNOWN);
  const previousStatusRef = useRef<ApiHealthStatus>(API_HEALTH_STATUS.UNKNOWN);
  const missedHeartbeatTicksRef = useRef(0);

  const setCheckingRef = useRef(setChecking);

  const clearInitialTimer = useCallback(() => {
    if (initialTimerRef.current) {
      clearTimeout(initialTimerRef.current);
      initialTimerRef.current = null;
    }
  }, []);

  const clearHeartbeatTimer = useCallback(() => {
    if (heartbeatTimerRef.current) {
      clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = null;
    }
  }, []);

  const markDecided = useCallback(() => {
    decisionRef.current = "decided";
    clearInitialTimer();
  }, [clearInitialTimer]);

  const applyHealthUpdate = useCallback(
    (nextStatus: ApiHealthStatus, nextSource: ApiHealthSource) => {
      setStatus(nextStatus, nextSource);
      setChecking(false);
      markDecided();
    },
    [markDecided, setChecking, setStatus],
  );

  const handleHealthPayload = useCallback(
    (payload: HealthPayload, nextSource: ApiHealthSource) => {
      lastHeartbeatRef.current = Date.now();

      // Backend only sends "ok" - invalid payloads are ignored
      if (!isValidHealthStatus(payload.status)) return;

      // Hysteresis: require consecutive successes to recover from DOWN/RECONNECTING
      const prevStatus = previousStatusRef.current;
      if (prevStatus === API_HEALTH_STATUS.DOWN || prevStatus === API_HEALTH_STATUS.RECONNECTING) {
        consecutiveSuccessRef.current += 1;
        if (consecutiveSuccessRef.current < CONSECUTIVE_SUCCESS_THRESHOLD) {
          return;
        }
      } else {
        consecutiveSuccessRef.current = 1;
      }

      applyHealthUpdate(API_HEALTH_STATUS.OK, nextSource);
    },
    [applyHealthUpdate],
  );

  const finalizeInitialDecision = useCallback(() => {
    if (decisionRef.current === "decided") return;
    applyHealthUpdate(API_HEALTH_STATUS.DOWN, "sse");
  }, [applyHealthUpdate]);

  // Latest-value refs so the bridge/timer callbacks never capture a stale closure.
  const handleHealthPayloadRef = useRef(handleHealthPayload);
  const applyHealthUpdateRef = useRef(applyHealthUpdate);
  const finalizeInitialDecisionRef = useRef(finalizeInitialDecision);
  const clearInitialTimerRef = useRef(clearInitialTimer);
  const clearHeartbeatTimerRef = useRef(clearHeartbeatTimer);

  useEffect(() => {
    setCheckingRef.current = setChecking;
    handleHealthPayloadRef.current = handleHealthPayload;
    applyHealthUpdateRef.current = applyHealthUpdate;
    finalizeInitialDecisionRef.current = finalizeInitialDecision;
    clearInitialTimerRef.current = clearInitialTimer;
    clearHeartbeatTimerRef.current = clearHeartbeatTimer;
  });

  // Arm the "opened but no heartbeat yet" DOWN countdown. Only started once the
  // socket is actually open, so the auth-init window (notifications not connected
  // yet) stays UNKNOWN instead of falsely flashing DOWN.
  const armInitialDecisionTimer = useCallback(() => {
    if (decisionRef.current === "decided" || initialTimerRef.current) return;
    initialTimerRef.current = setTimeout(() => {
      finalizeInitialDecisionRef.current();
    }, INITIAL_CHECK_TIMEOUT_MS);
  }, []);
  // `armInitialDecisionTimer` is a stable `useCallback([])`, so the ref initializer
  // captures it once — no per-render reassignment (which would mutate a ref during render).
  const armInitialDecisionTimerRef = useRef(armInitialDecisionTimer);

  // Observe the notifications-stream health bridge (no own socket).
  useEffect(() => {
    const unsubscribeFrames = subscribeToHealthFrames((payload) => {
      missedHeartbeatTicksRef.current = 0;
      handleHealthPayloadRef.current(payload, "sse");
    });
    const unsubscribeConnectionState = subscribeToHealthConnectionState((connected) => {
      if (connected) {
        // Socket open: give the backend a short window to send the first
        // heartbeat before declaring DOWN.
        armInitialDecisionTimerRef.current();
      } else if (decisionRef.current !== "decided") {
        // Socket dropped before we decided → DOWN. After the initial decision a
        // heartbeat gap (not the socket state) drives RECONNECTING/DOWN, so a
        // brief notifications reconnect (e.g. auth-token refresh) never flashes it.
        applyHealthUpdateRef.current(API_HEALTH_STATUS.DOWN, "sse");
      }
    });
    return () => {
      unsubscribeFrames();
      unsubscribeConnectionState();
    };
  }, []);

  // Main mount effect. If online, stay in checking/UNKNOWN and let the bridge's
  // first connection signal arm the DOWN countdown — no blind timer that would
  // fire before notifications has had a chance to connect.
  useEffect(() => {
    if (!navigator.onLine) {
      lastNonOfflineStatusRef.current = useApiHealthStore.getState().status;
      applyHealthUpdateRef.current(API_HEALTH_STATUS.OFFLINE, "offline");
    } else {
      setCheckingRef.current(true);
      decisionRef.current = "checking";
    }

    return () => {
      clearInitialTimerRef.current();
      clearHeartbeatTimerRef.current();
    };
  }, []);

  // Heartbeat monitoring — a sustained gap escalates RECONNECTING → DOWN. We no
  // longer reconnect here (notifications owns that); recovery comes when its
  // stream resumes and heartbeats flow again.
  useEffect(() => {
    if (decisionRef.current !== "decided" || status === API_HEALTH_STATUS.OFFLINE) {
      clearHeartbeatTimer();
      return clearHeartbeatTimer;
    }

    heartbeatTimerRef.current = setInterval(() => {
      const timeSinceLastHeartbeat = Date.now() - lastHeartbeatRef.current;
      const currentStatus = useApiHealthStore.getState().status;

      if (currentStatus === API_HEALTH_STATUS.DOWN || currentStatus === API_HEALTH_STATUS.OFFLINE) {
        return;
      }

      if (timeSinceLastHeartbeat > SSE_HEARTBEAT_TIMEOUT_MS) {
        missedHeartbeatTicksRef.current += 1;
        consecutiveSuccessRef.current = 0;

        if (missedHeartbeatTicksRef.current <= MAX_SILENT_RECONNECTS) {
          applyHealthUpdate(API_HEALTH_STATUS.RECONNECTING, "sse");
        } else {
          applyHealthUpdate(API_HEALTH_STATUS.DOWN, "sse");
        }
      }
    }, 5000);

    return clearHeartbeatTimer;
  }, [applyHealthUpdate, clearHeartbeatTimer, status]);

  // Online/offline handling — store-only; notifications owns the reconnect, so on
  // recovery we just re-open the initial-decision window and wait for heartbeats.
  useEffect(() => {
    const handleOffline = () => {
      lastNonOfflineStatusRef.current = useApiHealthStore.getState().status;
      applyHealthUpdate(API_HEALTH_STATUS.OFFLINE, "offline");
    };

    const handleOnline = () => {
      missedHeartbeatTicksRef.current = 0;
      setStatus(
        lastNonOfflineStatusRef.current === API_HEALTH_STATUS.OFFLINE
          ? API_HEALTH_STATUS.UNKNOWN
          : lastNonOfflineStatusRef.current,
        "sse",
      );
      setChecking(true);
      decisionRef.current = "checking";
      clearInitialTimer();
      // Don't arm a blind countdown here — the resumed heartbeat (≤5s) decides OK,
      // or notifications' reconnect emits a fresh open/error that re-arms it. A
      // 3s timer could otherwise fire DOWN just before the next 5s heartbeat.
    };

    window.addEventListener("offline", handleOffline);
    window.addEventListener("online", handleOnline);
    return () => {
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("online", handleOnline);
    };
  }, [applyHealthUpdate, clearInitialTimer, setChecking, setStatus]);

  // Analytics tracking + query recovery
  useEffect(() => {
    if (previousStatusRef.current !== status) {
      if (status === API_HEALTH_STATUS.DOWN) {
        trackEvent(METRICS_EVENTS.API_DOWN_SHOWN, { source });
      } else if (
        (previousStatusRef.current === API_HEALTH_STATUS.DOWN ||
          previousStatusRef.current === API_HEALTH_STATUS.RECONNECTING) &&
        status === API_HEALTH_STATUS.OK
      ) {
        if (previousStatusRef.current === API_HEALTH_STATUS.DOWN) {
          trackEvent(METRICS_EVENTS.API_DOWN_RECOVERED, { source });
        }
        queryClient.invalidateQueries({
          predicate: (query) => query.state.status === "error",
        });
      }
      previousStatusRef.current = status;
    }
  }, [queryClient, source, status, trackEvent]);

  return { status };
};
